import { useState, useEffect } from "react";
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MagnifyingGlass, Funnel, CaretLeft, CaretRight, CaretDown, CaretRight as CaretRightSm, ArrowCounterClockwise, Info } from "@phosphor-icons/react";
import AdminUserDialog from "@/components/AdminUserDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = {
  trial: "Trial", personal_base: "Base", personal_core: "Core", personal_top: "Top",
  business_starter: "Starter Aziendale", business_core: "Core Aziendale",
  business_top: "Aziendale Top", business_enterprise: "Enterprise",
};

const fmtEur = (v) => `€ ${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";

function ExpandedRow({ userId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/users/${userId}/workspaces-summary`, { withCredentials: true })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [userId]);
  if (items === null) return <div className="px-8 py-3 text-xs text-[#0D1F3C]/40">Caricamento workspace…</div>;
  if (items.length === 0) return <div className="px-8 py-3 text-xs text-[#0D1F3C]/40">Nessun workspace</div>;
  return (
    <div className="bg-[#F4F7FB]/60 px-8 py-3" data-testid={`expanded-row-${userId}`}>
      <table className="w-full text-xs">
        <thead className="text-[#0D1F3C]/60 uppercase">
          <tr><th className="text-left py-1">Workspace</th><th>Tipo</th><th className="text-center">Cand</th><th className="text-center">Dip</th><th className="text-center">Pos</th><th className="text-right">Status</th></tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.id} className="border-t border-[#0D1F3C]/8">
              <td className="py-1.5 font-medium text-[#0D1F3C]">{w.name || "—"}</td>
              <td className="text-center"><Badge variant="outline" className="text-[10px]">{w.type}</Badge></td>
              <td className="text-center">{w.candidates_count}</td>
              <td className="text-center">{w.employees_count}</td>
              <td className="text-center">{w.positions_count}</td>
              <td className="text-right">
                {w.status === "deleted"
                  ? <Badge className="bg-red-100 text-red-700 text-[10px]">Eliminato</Badge>
                  : <Badge className="bg-green-100 text-green-700 text-[10px]">Attivo</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set("search", search);
      if (type !== "all") params.set("type", type);
      if (plan !== "all") params.set("plan", plan);
      if (status !== "all") params.set("status", status);
      if (includeDeleted) params.set("include_deleted", "true");
      const { data } = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error("Errore caricamento utenti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, type, plan, status, includeDeleted]);

  const handleRestore = async (uid, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API}/admin/users/${uid}/restore`, {}, { withCredentials: true });
      toast.success("Utente ripristinato");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore ripristino"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4" data-testid="admin-users-page">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0D1F3C]">Console Admin · Utenti</h1>
            <p className="text-sm text-[#0D1F3C]/60 mt-1">{total} utenti · pagina {page}/{totalPages}</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="show-deleted" checked={includeDeleted} onCheckedChange={setIncludeDeleted} data-testid="toggle-show-deleted" />
            <label htmlFor="show-deleted" className="text-sm text-[#0D1F3C]/70 cursor-pointer">Mostra eliminati</label>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#0D1F3C]/8 rounded-xl p-4 flex flex-wrap gap-3 items-center" data-testid="admin-users-filters">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
            <Input data-testid="admin-users-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
              placeholder="Cerca per email, nome, azienda..." className="pl-9" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[140px]" data-testid="admin-users-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger className="w-[180px]" data-testid="admin-users-filter-plan"><SelectValue placeholder="Piano" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i piani</SelectItem>
              {Object.entries(PLAN_LABEL).map(([code, lbl]) => (
                <SelectItem key={code} value={code}>{lbl}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]" data-testid="admin-users-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="suspended">Sospesi</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="paid">Paganti</SelectItem>
              <SelectItem value="deleted">Eliminati</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { setPage(1); load(); }} data-testid="admin-users-apply-filters">
            <Funnel size={16} className="mr-2" />Applica
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
          <div className="max-h-[calc(100vh-340px)] overflow-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-[#0D1F3C] text-white text-left sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-3 w-8"></th>
                  <th className="px-3 py-3 font-semibold">Email</th>
                  <th className="px-3 py-3 font-semibold">Azienda</th>
                  <th className="px-3 py-3 font-semibold">Tipo</th>
                  <th className="px-3 py-3 font-semibold">Piano</th>
                  <th className="px-2 py-3 font-semibold text-center">
                    <Tooltip>
                      <TooltipTrigger className="inline-flex items-center gap-1">
                        Workspace<Info size={12} className="opacity-60" />
                      </TooltipTrigger>
                      <TooltipContent>Sottoclienti gestiti (agenzie) o spazio proprio (Personal)</TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="px-3 py-3 font-semibold text-right">Pagato</th>
                  <th className="px-3 py-3 font-semibold text-right">Prossimo</th>
                  <th className="px-3 py-3 font-semibold">Cadenza</th>
                  <th className="px-3 py-3 font-semibold">Stato</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-8 text-[#0D1F3C]/40">Nessun utente</td></tr>
                ) : items.map((u) => (
                  <React.Fragment key={u.id}>
                    <tr data-testid={`admin-user-row-${u.id}`}
                      onClick={() => setSelected(u.id)}
                      className={`border-t border-[#0D1F3C]/5 hover:bg-[#F4F7FB] cursor-pointer ${u.is_deleted ? "opacity-60" : ""}`}>
                      <td className="px-2 py-3 text-center" onClick={(e) => { e.stopPropagation(); setExpanded((s) => ({ ...s, [u.id]: !s[u.id] })); }}>
                        <button data-testid={`expand-row-${u.id}`} className="text-[#0D1F3C]/60 hover:text-[#1B4FD8]">
                          {expanded[u.id] ? <CaretDown size={14} /> : <CaretRightSm size={14} />}
                        </button>
                      </td>
                      <td className="px-3 py-3 font-medium text-[#0D1F3C]">
                        {u.email}
                        {u.is_admin && <Badge className="ml-2 bg-red-600 text-white text-[10px]">admin</Badge>}
                      </td>
                      <td className="px-3 py-3 text-[#0D1F3C]/80">{u.company_name || "—"}</td>
                      <td className="px-3 py-3">
                        <Badge className={u.account_type === "business" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                          {u.account_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-3"><Badge variant="outline">{PLAN_LABEL[u.plan_code] || u.plan_code}</Badge></td>
                      <td className="px-2 py-3 text-center font-semibold">{u.workspace_count}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{fmtEur(u.revenue_paid_total)}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="font-mono text-xs">{fmtEur(u.next_billing_amount)}</div>
                        {u.next_billing_date && <div className="text-[10px] text-[#0D1F3C]/50">{fmtDate(u.next_billing_date)}</div>}
                      </td>
                      <td className="px-3 py-3">
                        {u.billing_cycle === "yearly"
                          ? <Badge className="bg-green-100 text-green-700">Annuale</Badge>
                          : u.billing_cycle === "monthly"
                          ? <Badge className="bg-blue-100 text-blue-700">Mensile</Badge>
                          : <span className="text-xs text-[#0D1F3C]/40">—</span>}
                      </td>
                      <td className="px-3 py-3">
                        {u.is_deleted ? (
                          <div className="flex items-center gap-1">
                            <Badge className="bg-red-100 text-red-700">Eliminato</Badge>
                            <Button size="sm" variant="ghost" onClick={(e) => handleRestore(u.id, e)} data-testid={`restore-btn-${u.id}`} className="h-6 w-6 p-0">
                              <ArrowCounterClockwise size={12} />
                            </Button>
                          </div>
                        ) : u.is_suspended ? (
                          <Badge className="bg-orange-100 text-orange-700">Sospeso</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                        )}
                      </td>
                    </tr>
                    {expanded[u.id] && (
                      <tr><td colSpan={10} className="p-0"><ExpandedRow userId={u.id} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-[#F4F7FB] border-t border-[#0D1F3C]/5">
            <span className="text-xs text-[#0D1F3C]/60">{total} risultati totali</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="admin-users-prev"><CaretLeft size={14} /></Button>
              <span className="text-sm font-medium text-[#0D1F3C]">{page} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="admin-users-next"><CaretRight size={14} /></Button>
            </div>
          </div>
        </div>

        {selected && (<AdminUserDialog userId={selected} onClose={() => setSelected(null)} onChanged={load} />)}
      </div>
    </TooltipProvider>
  );
}
